import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Typography,
  TextField,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateFinalInvoice, useDepartments, useOrderInvoices } from '@/api/hooks'
import type { OrderDetail } from '@/api/types'
import { ErrorBanner, formatMoney, isoDate } from '@/components/common'

/**
 * Raises the final tax invoice straight from the order being viewed, which is
 * where the person closing an event actually is.
 */
export function FinalInvoiceDialog({
  order,
  onClose,
}: {
  order: OrderDetail | null
  onClose: () => void
}) {
  const navigate = useNavigate()
  const create = useCreateFinalInvoice()
  const { data: existing } = useOrderInvoices(order?.id)

  const [invoiceDate, setInvoiceDate] = useState(() => isoDate(0))
  const [dueDate, setDueDate] = useState(() => isoDate(7))
  const [interState, setInterState] = useState(false)
  // Electricity, hall cleaning and recoveries: the venue rather than the food,
  // taxed at eighteen, and often settled by somebody else entirely.
  const [separateAmenities, setSeparateAmenities] = useState(false)
  // On by default, which is what the office asked for: the food billed at five
  // on its own document and the transport, power and cleaning at eighteen on
  // another. It subsumes the amenities split, which is one rate band under
  // another name.
  const [perTaxRate, setPerTaxRate] = useState(true)
  const [notes, setNotes] = useState('')
  // Whose budget it comes out of. Asked here because the server will not issue
  // a giveaway that does not say, and a draft raised without one is a document
  // nobody can finish - which is exactly what happened the first time.
  const [departmentId, setDepartmentId] = useState('')
  const { data: departments } = useDepartments()

  if (!order) return null

  const givenAway = order.complimentary

  // An order normally carries only the advance request until the event is done.
  const alreadyFinal = (existing ?? []).some(
    (invoice) =>
      invoice.invoiceType === (givenAway ? 'COMPLIMENTARY' : 'TAX_INVOICE')
      && invoice.status !== 'CANCELLED',
  )
  // The server refuses the tax invoice without one, so say so here rather than
  // letting the person fill the form in and be turned away on submit.
  //
  // A giveaway is the exception, and the server makes the same exception: a
  // proforma exists so a customer can approve figures before they are committed
  // to, and nobody is being committed to anything here.
  const hasProforma = givenAway || (existing ?? []).some(
    (invoice) => invoice.invoiceType === 'PROFORMA' && invoice.status !== 'CANCELLED',
  )

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Final invoice — {order.orderNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={create.error} />

        {!hasProforma ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            No proforma has been raised for this order. Send the customer a proforma first — the tax
            invoice is the document that commits them, so they should have seen the figures before it
            reaches them.
          </Alert>
        ) : alreadyFinal ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This order already has a tax invoice. Raising another will bill the customer twice unless the
            first one is cancelled.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Built from the order snapshot including approved change requests. Advance already received is
            set off against it.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Invoice date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Due date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={interState} onChange={(event) => setInterState(event.target.checked)} />
              }
              label="Inter-state supply (IGST instead of CGST + SGST)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={perTaxRate} onChange={(event) => setPerTaxRate(event.target.checked)} />
              }
              label="One invoice per GST rate"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              {perTaxRate
                ? 'The catering at 5% on its own bill; transport, power and cleaning at 18% on another. Each is a tax invoice with its own number.'
                : 'One invoice carrying every rate, with the tax summary at the foot saying what each came to.'}
            </Typography>
          </Grid>
          {/* Only worth asking when the rates are not already being split:
              amenities are the 18% band under another name. */}
          {!perTaxRate ? (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={separateAmenities}
                    onChange={(event) => setSeparateAmenities(event.target.checked)}
                  />
                }
                label="Bill amenities separately (electricity, hall cleaning, recoveries)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                {separateAmenities
                  ? 'Two invoices: the catering on one, the venue’s own charges on the other at 18%.'
                  : 'One invoice, with the venue’s charges as lines on it at 18%.'}
              </Typography>
            </Grid>
          ) : null}
          {givenAway ? (
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                required
                label="Given away by"
                value={departmentId}
                onChange={(event) => setDepartmentId(event.target.value)}
                helperText="Whose budget it comes out of"
              >
                {(departments ?? []).map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mt: 2 }}>
          Order value {formatMoney(order.totalAmount)} · received {formatMoney(order.paidAmount)} ·
          balance {formatMoney(order.balanceAmount)}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={create.isPending || !hasProforma || (givenAway && !departmentId)}
          onClick={() =>
            create.mutate(
              {
                orderId: order.id,
                invoiceDate,
                dueDate,
                interState,
                separateAmenitiesInvoice: separateAmenities,
                separateByTaxRate: perTaxRate,
                notes: notes || undefined,
                departmentId: givenAway ? departmentId : undefined,
              },
              {
                onSuccess: () => {
                  onClose()
                  navigate('/invoices')
                },
              },
            )
          }
        >
          Raise invoice
        </Button>
      </DialogActions>
    </Dialog>
  )
}
